import * as React from 'react';

import { DescriptionPage_SelfServeConfiguration_AboutModule_SubsectionName as AboutModuleSubsectionName } from '__generated__/graphql-types';

import LearnerIncentiveModuleRenderer from 'bundles/unified-description-page-common/module-renderers/LearnerIncentiveModuleRenderer';
import PageBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/PageBannerModuleRenderer';
import TestimonialsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/TestimonialsModuleRenderer';
import CareerOutcomesModuleRenderer from 'bundles/unified-description-page-common/module-renderers/career-outcomes/ConsumerSpecializationCareerOutcomesModuleRenderer';
import PathwaysCareerOutcomesModuleRenderer from 'bundles/unified-description-page-common/module-renderers/career-outcomes/ConsumerSpecializationPathwaysCareerOutcomesModuleRenderer';
import FAQsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/faqs/FAQsModuleRenderer';
import ProductOverviewModuleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/ConsumerSpecializationProductOverviewModuleRenderer';
import SyllabusModuleRenderer from 'bundles/unified-description-page-common/module-renderers/syllabus/SpecializationSyllabusModuleRenderer';
import type { ConsumerSpecializationMarketingPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationMarketingPageQuery';
import {
  KeyInformationItemNames,
  ModuleNames,
  ProductDetailItemNames,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import type {
  PageConfig,
  ProductOverviewModuleConfig,
} from 'bundles/unified-description-page-common/types/moduleConfig';

import SpecializationsHeroImage from 'images/unified-description-page/specializations-hero-image.png';

type Props = {
  pageData: ConsumerSpecializationMarketingPageFieldsFragment;
  pageConfig?: PageConfig | null;
};

const Renderer = ({ pageData, pageConfig }: Props) => {
  if (!pageConfig || !pageConfig.modules.length) {
    return null;
  }

  const shouldShowPathwaysXDP = !!pageData?.isCareerRolePathway;

  const modulesToRender = pageConfig.modules.map((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        return (
          <ProductOverviewModuleRenderer
            pageData={pageData}
            config={
              {
                ...moduleConfig,
                showHeroLanguageInfo: false,
                backgroundImage: SpecializationsHeroImage,
                removeLinksAboveFold: true,
                keyInfoModule: {
                  items: [
                    { name: KeyInformationItemNames.ContentCount },
                    { name: KeyInformationItemNames.Reviews },
                    { name: KeyInformationItemNames.Difficulty },
                    { name: KeyInformationItemNames.Duration },
                    { name: KeyInformationItemNames.Schedule },
                    { name: KeyInformationItemNames.Stackability },
                  ],
                },
                aboutModule: {
                  subsections: [
                    { name: AboutModuleSubsectionName.LearningOutcomes },
                    { name: AboutModuleSubsectionName.Skills },
                    { name: AboutModuleSubsectionName.DetailsToKnow },
                  ],
                },
                productDetailItems: [
                  ProductDetailItemNames.ShareableCertificate,
                  ProductDetailItemNames.IndustryCertification,
                  ProductDetailItemNames.LanguagesWithMLInfo,
                  ProductDetailItemNames.RecentlyUpdated,
                ],
              } as ProductOverviewModuleConfig
            }
            key={moduleConfig.name}
          />
        );
      case ModuleNames.CareerOutcomes:
        if (shouldShowPathwaysXDP) {
          return <PathwaysCareerOutcomesModuleRenderer pageData={pageData} key={moduleConfig.name} />;
        }

        return <CareerOutcomesModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.LearnerIncentive:
        return <LearnerIncentiveModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Syllabus:
        return (
          <SyllabusModuleRenderer
            pageData={pageData}
            config={{ disableCta: true, disableAceCta: true }}
            key={moduleConfig.name}
          />
        );
      case ModuleNames.Testimonials:
        return <TestimonialsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Faqs:
        return <FAQsModuleRenderer pageData={pageData} config={{ disableCta: true }} key={moduleConfig.name} />;
      case ModuleNames.PageBanner:
        return <PageBannerModuleRenderer key={moduleConfig.name} />;
      default:
        return null;
    }
  });

  return <>{modulesToRender}</>;
};

export default Renderer;
